;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:propstrata-app":true});}catch(e){}}();import {
	ACTIONS,
	type Controls,
	EVENTS,
	type EventData,
	type EventHandler,
	STATUS,
	type Step,
} from "react-joyride";
import { closeTourOverlayGroup } from "@/features/onboarding-tours/orchestrator/tour-overlay-groups";
import { runTourStepEffect } from "@/features/onboarding-tours/orchestrator/tour-step-actions";
import {
	resolveSkippedForwardIndex,
	resolveStepIndexAfterAction,
} from "@/features/onboarding-tours/orchestrator/tour-step-navigation";
import type {
	TourId,
	TourStepData,
} from "@/features/onboarding-tours/types/tour-definition";

export interface TourJoyrideEventHandlerParams {
	activeTourId: TourId | null;
	steps: Step[];
	setRun: (run: boolean) => void;
	setStepIndex: (stepIndex: number) => void;
	onTourComplete: ({ tourId }: { tourId: TourId }) => void;
	onTourSkip: ({ tourId }: { tourId: TourId }) => void;
	shouldDeferStepAdvance?: ({ index }: { index: number }) => boolean;
}

function getStepData({ step }: { step: Step | undefined }): TourStepData {
	return (step?.data ?? {}) as TourStepData;
}

function getStepOverlayGroup({
	step,
}: {
	step: Step | undefined;
}): string | undefined {
	const stepData = (step?.data ?? {}) as TourStepData;
	return stepData.overlayGroup;
}

function closeOverlayGroupWhenLeaving({
	fromIndex,
	toIndex,
	steps,
}: {
	fromIndex: number;
	toIndex: number;
	steps: Step[];
}): void {
	if (toIndex < 0 || toIndex >= steps.length) {
		return;
	}

	const fromGroup = getStepOverlayGroup({ step: steps[fromIndex] });
	const toGroup = getStepOverlayGroup({ step: steps[toIndex] });

	if (fromGroup && fromGroup !== toGroup) {
		closeTourOverlayGroup({ overlayGroup: fromGroup });
	}
}

export function createTourJoyrideEventHandler({
	activeTourId,
	steps,
	setRun,
	setStepIndex,
	onTourComplete,
	onTourSkip,
	shouldDeferStepAdvance,
}: TourJoyrideEventHandlerParams): EventHandler {
	return (data: EventData, controls: Controls) => {
		const { action, index, status, type } = data;

		if (type === EVENTS.STEP_BEFORE_HOOK) {
			const skippedForwardIndex = resolveSkippedForwardIndex({
				index,
				steps,
			});
			if (skippedForwardIndex !== undefined && skippedForwardIndex !== index) {
				controls.go(skippedForwardIndex);
				setStepIndex(skippedForwardIndex);
				return;
			}
		}

		if (type === EVENTS.STEP_AFTER) {
			if (action === ACTIONS.PREV) {
				const previousIndex =
					resolveStepIndexAfterAction({ action, index, steps }) ??
					Math.max(index - 1, 0);
				closeOverlayGroupWhenLeaving({
					fromIndex: index,
					steps,
					toIndex: previousIndex,
				});
				if (previousIndex !== index - 1) {
					controls.go(previousIndex);
				}
				setStepIndex(previousIndex);
				return;
			}

			if (action === ACTIONS.NEXT || action === ACTIONS.CLOSE) {
				const deferAdvance = shouldDeferStepAdvance?.({ index });
				if (deferAdvance) {
					return;
				}

				const stepData = getStepData({ step: steps[index] });
				if (action === ACTIONS.NEXT && stepData.advanceOnNext?.effect) {
					runTourStepEffect({ effect: stepData.advanceOnNext.effect });
				}

				const nextIndex =
					resolveStepIndexAfterAction({ action, index, steps }) ?? index + 1;
				closeOverlayGroupWhenLeaving({
					fromIndex: index,
					steps,
					toIndex: nextIndex,
				});
				if (nextIndex !== index + 1) {
					controls.go(nextIndex);
				}
				setStepIndex(nextIndex);
				return;
			}
		}

		if (type === EVENTS.TARGET_NOT_FOUND) {
			return;
		}

		if (type === EVENTS.TOUR_END) {
			if (!activeTourId) {
				setRun(false);
				setStepIndex(0);
				return;
			}

			setRun(false);
			setStepIndex(0);

			if (status === STATUS.FINISHED) {
				onTourComplete({ tourId: activeTourId });
				return;
			}

			if (status === STATUS.SKIPPED) {
				onTourSkip({ tourId: activeTourId });
			}

			void controls;
		}
	};
}
